/**
 * Collaboration in a shared project, phase 2 (vfs-service src/collab): who changed what (changes by person, blame),
 * reverting one person's or one agent run's changes, comments with @mentions, the person's in-app inbox (kept by the
 * sandbox gateway) and "Live now" across the projects shared with them. Every call goes through vfs-service's base URL
 * with the person's authorized fetch, as the rest of the vfs client does.
 *
 * Authors everywhere: `{ id: 'user:<sub>', sub, name, initials, slot }` (slot: the person's presence colour slot).
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { shareInflight } from '../utils/sharedRequest';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const projectUrl = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}${path}`;

/** An HTTP error with the status and the JSON body vfs-service answered (e.g. a revert's 409 conflicts). */
export class CollabError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'CollabError';
    this.status = status;
    this.body = body;
  }
}

async function call(url, { method = 'GET', body, cache } = {}) {
  if (!base()) { throw new CollabError('vfs-service is not configured', 0, null); }
  const response = await getAuthorizedFetch()(url, {
    method,
    ...(cache ? { cache } : {}),
    headers: {
      Accept: 'application/json',
      ...buildAclContextHeaders(),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    throw new CollabError(data?.message || data?.error || `Request failed (${response.status})`, response.status, data);
  }
  return data;
}

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') { search.set(key, String(value)); }
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

/** `{ you, changes, runs, authors }`: the project's changes, newest first. `since`: an ISO time or nothing (all). */
export function getChanges(projectId, { since, limit } = {}) {
  return call(projectUrl(projectId, `/collab/changes${query({ since, limit })}`));
}

/** `{ path, lines: [{ line, sha, timestamp, author, via, aiModel, agent, uncommitted }] }` for one file. */
export function getBlame(projectId, path) {
  return call(projectUrl(projectId, `/collab/blame${query({ path })}`));
}

/**
 * Reverts the given commits as a new commit by the caller. `dryRun`: only checks. Resolves to `{ ok, files, commits }`;
 * rejects with a CollabError whose `status` is 409 and `body.conflicts` the overlapping lines when others built on them.
 */
export function revertChanges(projectId, shas, { dryRun = false } = {}) {
  return call(projectUrl(projectId, '/collab/revert'), { method: 'POST', body: { shas, dryRun } });
}

/** `{ threads }`: the comment threads of one file (`path`) or of the whole project. */
export function getComments(projectId, { path } = {}) {
  return call(projectUrl(projectId, `/collab/comments${query({ path })}`));
}

/** A new comment (or a reply with `parentId`). `mentions`: the emails of the members picked with @. */
export function addComment(projectId, { path, line, body, mentions = [], parentId } = {}) {
  return call(projectUrl(projectId, '/collab/comments'), {
    method: 'POST',
    body: { path, ...(Number.isInteger(line) ? { line } : {}), body, mentions, ...(parentId ? { parentId } : {}) },
  });
}

export function resolveComment(projectId, commentId, resolved = true) {
  return call(projectUrl(projectId, `/collab/comments/${encodeURIComponent(commentId)}/resolve`), { method: 'POST', body: { resolved } });
}

/** `{ role, owner, members }`: who is in the project (for the @mention picker). */
export function getAccess(projectId) {
  return call(projectUrl(projectId, '/access'));
}

/** The person's inbox `{ items, unread }`, or null when the gateway has none (404). */
export async function getInbox({ limit = 50 } = {}) {
  try {
    // Polled live (hooks/useInbox.js): never an answer from the browser's cache; one request while one is in flight.
    return await shareInflight(`inbox:${limit}`, () => call(`${base()}/api/v1/me/inbox${query({ limit })}`, { cache: 'no-store' }));
  } catch (error) {
    if (error.status === 404 || error.status === 0) { return null; }
    throw error;
  }
}

export function markInboxRead({ ids, all } = {}) {
  return call(`${base()}/api/v1/me/inbox/read`, { method: 'POST', body: all ? { all: true } : { ids: ids || [] } });
}

/** `{ projects }` with someone active now, or null when not available (404). */
export async function getLiveNow() {
  try {
    return await shareInflight('live-now', () => call(`${base()}/api/v1/live`));
  } catch (error) {
    if (error.status === 404 || error.status === 0) { return null; }
    throw error;
  }
}

// ---- Phase 3: handoff notes, viewer suggestions, the activity timeline ----

/** `{ notes }`: the project's handoff notes, newest first (`seen`: you dismissed it). */
export function getHandoffs(projectId, { limit = 20 } = {}) {
  return call(projectUrl(projectId, `/collab/handoffs${query({ limit })}`));
}

/** `{ notes }`: notes by other people you have not dismissed, newest first. */
export function getUnseenHandoffs(projectId) {
  return call(projectUrl(projectId, '/collab/handoffs/unseen'));
}

/** Leaves a note `{ done, next?, questions?, to?: [email], since? }`; resolves to `{ note }`. */
export function addHandoff(projectId, { done, next, questions, to = [], since } = {}) {
  return call(projectUrl(projectId, '/collab/handoffs'), {
    method: 'POST',
    body: { done, ...(next ? { next } : {}), ...(questions ? { questions } : {}), to, ...(since ? { since } : {}) },
  });
}

/**
 * Edits a handoff note (`{ done?, next?, questions? }`, the writer or the project's owner: `note.canChange`); resolves to
 * `{ note }` (with `editedAt`). The notes are the project's, on its owner's pod: every collaborator sees the change.
 */
export function editHandoff(projectId, noteId, { done, next, questions } = {}) {
  return call(projectUrl(projectId, `/collab/handoffs/${encodeURIComponent(noteId)}`), { method: 'PATCH', body: { done, next, questions } });
}

/** Deletes a handoff note for everyone (the writer or the owner); resolves to `{ deleted }`. */
export function deleteHandoff(projectId, noteId) {
  return call(projectUrl(projectId, `/collab/handoffs/${encodeURIComponent(noteId)}`), { method: 'DELETE' });
}

export function dismissHandoff(projectId, noteId) {
  return call(projectUrl(projectId, `/collab/handoffs/${encodeURIComponent(noteId)}/dismiss`), { method: 'POST', body: {} });
}

/** `{ draft: { done, next, questions }, since, changes }`: a plain draft from your own changes since `since`. */
export function getHandoffDraft(projectId, { since } = {}) {
  return call(projectUrl(projectId, `/collab/handoffs/draft${query({ since })}`));
}

/** `{ sha, content }`: the file as last committed, the base of a suggestion. */
export function getSuggestionBase(projectId, path) {
  return call(projectUrl(projectId, `/collab/suggestions/base${query({ path })}`));
}

/** Submits a suggestion `{ path, baseSha, content, message }`; resolves to `{ suggestion }`. */
export function addSuggestion(projectId, { path, baseSha, content, message }) {
  return call(projectUrl(projectId, '/collab/suggestions'), { method: 'POST', body: { path, baseSha, content, message } });
}

/** `{ suggestions, canDecide }`. `status`: 'pending' (default) or 'all'. */
export function getSuggestions(projectId, { status = 'pending', path } = {}) {
  return call(projectUrl(projectId, `/collab/suggestions${query({ status, path })}`));
}

/** Applies a suggestion (a commit credited to its writer); a CollabError 409 with `body.conflicts` when it no longer fits. */
export function acceptSuggestion(projectId, id) {
  return call(projectUrl(projectId, `/collab/suggestions/${encodeURIComponent(id)}/accept`), { method: 'POST', body: {} });
}

export function rejectSuggestion(projectId, id, reason = '') {
  return call(projectUrl(projectId, `/collab/suggestions/${encodeURIComponent(id)}/reject`), { method: 'POST', body: { reason } });
}

/** `{ events, people }`: the project's timeline, newest first. */
export function getActivity(projectId, { since, until, limit = 300 } = {}) {
  return call(projectUrl(projectId, `/collab/activity${query({ since, until, limit })}`));
}

// ---- Phase 4: since you last left, agent notes, conflicts, watching an agent ----

/**
 * What others changed since you last left the project: `{ since, changes, files: [{ path, count, at, by: [{ key, name,
 * initials, slot, agent, via }] }], authors: [{ key, kind, author, via, count }] }`. `since` null: a first visit.
 */
export function getSinceLastSeen(projectId) {
  return call(projectUrl(projectId, '/collab/since-last-seen'));
}

/**
 * `{ notes }`: notes between AI agents and people (vfs-service src/collab/notes.js), newest first; `path`: one file's.
 * A note: `{ id, at, by: { name, initials, slot, kind: 'agent'|'person', label, agentId }, from: "Flow chat for Luca",
 * to: 'agents'|'people'|<email>, agent, path, line, text, editedAt, canChange }` (`canChange`: you wrote it, or drive the
 * agent that did, or own the project).
 */
export function getNotes(projectId, { path, limit } = {}) {
  return call(projectUrl(projectId, `/collab/notes${query({ path, limit })}`));
}

/**
 * Leaves a note: for the AI agents (`to: 'agents'`, `agent`: one agent's presence id only, as "Tell the agent" does),
 * for everyone (`'people'`) or one member (their email), optionally about a file's line. Resolves to `{ note }`.
 */
export function addNote(projectId, { to = 'agents', agent, path, line, text }) {
  return call(projectUrl(projectId, '/collab/notes'), {
    method: 'POST',
    body: { to, text, ...(agent ? { agent } : {}), ...(path ? { path } : {}), ...(Number.isInteger(line) ? { line } : {}) },
  });
}

/**
 * `{ conflicts }`: whole-file writes that overlapped live typing in the last 10 minutes (vfs-service
 * src/collab/conflicts.js; presence carries the same list). A conflict: `{ id, path, at, until, writer: { kind, agentId,
 * label, name }, live: [{ name, sub }], lines: [[from, to]], hunks: [{ merged: [from, to], live: [..], writer: [..],
 * kept: [..] }] }`.
 */
export function getConflicts(projectId, { path } = {}) {
  return call(projectUrl(projectId, `/collab/conflicts${query({ path })}`));
}

/** Keeps one side of a conflict: 'merged' (as it is), 'live' (the typed lines) or 'writer'. Resolves to `{ kept, sha }`. */
export function resolveConflict(projectId, id, keep) {
  return call(projectUrl(projectId, `/collab/conflicts/${encodeURIComponent(id)}/resolve`), { method: 'POST', body: { keep } });
}

/** "Notify me when it's done": a notification when that agent's run ends (`watch: false` stops). `{ watching }`. */
export function watchAgent(projectId, agentId, watch = true) {
  return call(projectUrl(projectId, `/collab/agents/${encodeURIComponent(agentId)}/watch`), { method: 'POST', body: { watch } });
}

/** You saw everything up to now: the "modified" marks go. Resolves to `{ since }`. */
export function markProjectSeen(projectId) {
  return call(projectUrl(projectId, '/collab/seen'), { method: 'POST', body: {} });
}

/** Edits a note's text (`note.canChange`); resolves to `{ note }`. Agents get the new words. */
export function editNote(projectId, noteId, text) {
  return call(projectUrl(projectId, `/collab/notes/${encodeURIComponent(noteId)}`), { method: 'PATCH', body: { text } });
}

/** Deletes a note for everyone, people and agents (`note.canChange`); resolves to `{ deleted }`. */
export function deleteNote(projectId, noteId) {
  return call(projectUrl(projectId, `/collab/notes/${encodeURIComponent(noteId)}`), { method: 'DELETE' });
}

/**
 * Messages, the chat between the project's people (vfs-service src/collab/chat.js): a message to everyone (`to:
 * 'people'`) or to one member (their email), optionally about a file's line. Anyone who can open the project sends one,
 * a viewer too; Everyone's only notify the people it @mentions. Resolves to `{ note }` (with `canChange`).
 */
export function sendMessage(projectId, { to = 'people', text, path, line, mentions }) {
  return call(projectUrl(projectId, '/collab/messages'), {
    method: 'POST',
    body: { to, text, ...(path ? { path } : {}), ...(Number.isInteger(line) ? { line } : {}), ...(mentions?.length ? { mentions } : {}) },
  });
}

/** Edits your message (a viewer's too); resolves to `{ note }`. */
export function editMessage(projectId, noteId, text) {
  return call(projectUrl(projectId, `/collab/messages/${encodeURIComponent(noteId)}`), { method: 'PATCH', body: { text } });
}

/** Deletes your message for everyone; resolves to `{ deleted }`. */
export function deleteMessage(projectId, noteId) {
  return call(projectUrl(projectId, `/collab/messages/${encodeURIComponent(noteId)}`), { method: 'DELETE' });
}
